import {useAxiosClient} from "~/use/axios/client";
import {CustomerApi, GmModelLibraryModelsAccountModel} from "~/gen/openapi/sblService";
import {keepPreviousData, useMutation, useQuery, UseQueryOptions} from "@tanstack/vue-query";
import {MS_HOUR} from "~/constants/time";
import {useOnline} from "@vueuse/core";
import {Customer} from "~/models/customers";
import {queryClient} from "~/plugins/vue-query";

export const updateCustomerInCache = (customer: GmModelLibraryModelsAccountModel | Customer) => {
    queryClient.setQueryData(['customerGetCustomersV2'], (oldData: Array<GmModelLibraryModelsAccountModel>) => {
        const customerIndex = oldData.findIndex((c: GmModelLibraryModelsAccountModel) => c.Id === customer.Id)
        const newData = [...oldData]
        if (customerIndex > -1) {
            newData.splice(customerIndex, 1, customer as GmModelLibraryModelsAccountModel)
        } else {
            newData.push(customer as GmModelLibraryModelsAccountModel)
        }
        return newData
    })
}

export const useCustomerService = () => {
    const { axiosClient } = useAxiosClient()
    const customerApi = new CustomerApi(undefined, '', axiosClient.value)
    const isOnline = useOnline()

    const getCustomersQueryOptions = {
        queryKey: ['customerGetCustomersV2'],
        queryFn: async () => {
            try {
                return (await customerApi.customerGetCustomersV2())?.data?.filter((c) => !c.ToBeRemoved)?.sort((a, b) => a.Name!.localeCompare(b.Name!)) || []
            } catch (error) {
                console.error(error)

                const previousData = queryClient.getQueryData(['customerGetCustomersV2'])
                if (previousData) {
                    return previousData as Array<GmModelLibraryModelsAccountModel>
                }
                return []
            }
        },
        staleTime: 3*MS_HOUR,
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
        networkMode: 'offlineFirst',
        gcTime: Infinity,
        enabled: isOnline,
    }

    const customersQuery = () => useQuery({
        queryKey: getCustomersQueryOptions.queryKey,
        queryFn: getCustomersQueryOptions.queryFn,
        staleTime: getCustomersQueryOptions.staleTime,
        refetchOnWindowFocus: getCustomersQueryOptions.refetchOnWindowFocus,
        placeholderData: getCustomersQueryOptions.placeholderData,
        networkMode: 'offlineFirst',
        gcTime: getCustomersQueryOptions.gcTime,
        enabled: getCustomersQueryOptions.enabled,
    })

    const getCustomerQueryOptions = (id: string): UseQueryOptions<GmModelLibraryModelsAccountModel | undefined> => {
        return {
            queryKey: ['customerGetCustomerV2', id],
            queryFn: async () => {
                try {
                    const result = (await customerApi.customerGetCustomerV2(id))?.data
                    if (result?.length === 1) {
                        return result[0] as GmModelLibraryModelsAccountModel
                    }
                    return {}
                } catch (error) {
                    console.error(error)
                }
            },
            staleTime: MS_HOUR,
            refetchOnWindowFocus: false,
            placeholderData: keepPreviousData,
            networkMode: 'offlineFirst',
            gcTime: MS_HOUR,
        }
    }

    const getCustomerById = async (id: string): Promise<Customer | null> => {
        const customers = await queryClient.ensureQueryData({
            queryKey: getCustomersQueryOptions.queryKey,
            queryFn: getCustomersQueryOptions.queryFn,
            revalidateIfStale: false,
        }) as Array<GmModelLibraryModelsAccountModel>

        let customer = customers?.find((c) => c.Id === id) as Customer | undefined

        if (!customer) {
            const options = getCustomerQueryOptions(id)
            try {
                const result = await queryClient.ensureQueryData(options)
                if (result) {
                    updateCustomerInCache(result)
                    customer = result as Customer
                }
            } catch (error) {
                console.error('Failed to fetch customer:', error)
                return null
            }
        }

        return customer ?? null
    }

    const customerQuery = (id: string) => useQuery({
        queryKey: ['customer', id],
        queryFn: () => getCustomerById(id),
    })


    const customerMutation = () => useMutation({
        mutationFn: async (customer: GmModelLibraryModelsAccountModel | Customer) => {
            return customerApi.customerUpdateCustomer(customer.Id!, customer as GmModelLibraryModelsAccountModel)
        },
        onSuccess(_, c) {
            updateCustomerInCache(c)
        },
        onError(error) {
            console.error(error)
        }
    })

    return {
        getCustomersQueryOptions,
        getCustomerQueryOptions,
        customersQuery,
        updateCustomerInCache,
        customerMutation,
        customerQuery,
        getCustomerById,
    }

}